// src/utils/csrfFetch.js
import { getApiBase } from './apiBase';

const CSRF_COOKIE = 'inboxops_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';

// Routes we skip (OAuth/webhooks/static/add-ins)
const SKIP_EXACT = new Set([
  '/api/meetings/webhook',
  '/auth/google/login',
  '/auth/microsoft/login',
  '/auth',
  '/oauth2callback',
  '/auth/microsoft',
  '/ms-auth-callback',
  '/auth/microsoft/plugin-init',
  '/auth/microsoft/plugin-exchange',
  '/asana-callback',
]);
const SKIP_PREFIXES = ['/outlook', '/api/plugin'];

export function pathIsSkipped(pathname) {
  if (SKIP_EXACT.has(pathname)) return true;
  return SKIP_PREFIXES.some(p => pathname.startsWith(p));
}

export function getCookie(name) {
  if (typeof document === 'undefined') return null;

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split('; ')
    .find(r => r.startsWith(prefix));

  if (!cookie) return null;

  const value = cookie.slice(prefix.length);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// Decide if a URL is aimed at our API (vs third-party).
// Heuristics: matches configured BACKEND origin OR path looks like an app API path.
function getBackendOrigin() {
  try {
    const backendUrl = getApiBase();
    return backendUrl ? new URL(backendUrl, window.location.origin).origin : null;
  } catch {
    return null;
  }
}

function isBodyWithBrowserManagedContentType(body) {
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
  );
}

export function isOurApi(urlLike) {
  try {
    const u = new URL(urlLike, window.location.origin);
    const backendOrigin = getBackendOrigin();
    if (backendOrigin && u.origin === backendOrigin) return true;
    if (u.origin !== window.location.origin) return false;
    // Support setups that proxy /api on the app domain in dev
    return u.pathname.startsWith('/api') || u.pathname.startsWith('/auth');
  } catch {
    return false;
  }
}

export function isUnsafeMethod(method = 'GET') {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(method || 'GET').toUpperCase());
}

export function shouldAttachCsrf(urlLike, method = 'GET') {
  if (!isUnsafeMethod(method)) return false;
  if (!isOurApi(urlLike)) return false;

  try {
    const pathname = new URL(urlLike, window.location.origin).pathname;
    return !pathIsSkipped(pathname);
  } catch {
    return true;
  }
}

export async function getCsrfTokenFor(urlLike) {
  // Try cookie first (works if same-site cookie is visible here)
  const fromCookie = getCookie(CSRF_COOKIE);
  if (fromCookie) return fromCookie;

  // Otherwise hit the same origin the request is going to: <that-origin>/api/csrf
  try {
    const u = new URL(urlLike, window.location.origin);
    const csrfUrl = `${u.origin}/api/csrf`;
    const res = await window.fetch(csrfUrl, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    return data?.token || null;
  } catch {
    return null;
  }
}

export function installCsrfFetch() {
  if (typeof window === 'undefined' || !window.fetch) return;
  if (window.__inboxops_csrf_fetch_installed) return; // idempotent
  window.__inboxops_csrf_fetch_installed = true;

  const orig = window.fetch;
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input?.url || '');
    const method = (init.method || 'GET').toUpperCase();

    if (shouldAttachCsrf(url, method)) {
      const token = await getCsrfTokenFor(url);
      const headers = new Headers(init.headers || {});
      headers.set(CSRF_HEADER, token || '');
      init.headers = headers;

      if (init.credentials == null) init.credentials = 'include';
      // Strongly encourage JSON-only writes (helps CSRF posture)
      if (
        init.body != null &&
        !isBodyWithBrowserManagedContentType(init.body)
      ) {
        const h = (init.headers || {});
        const headers = h instanceof Headers ? h : new Headers(h);
        if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
        init.headers = headers;
      }
    }
    return orig(input, init);
  };
}
